import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-421f0f32"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["aria-busy"]

import { computed, onUnmounted, ref, watch } from "vue";


export default /*#__PURE__*/_defineComponent({
  props: {
    loading: { type: Boolean },
    resolvedText: null,
    placeholder: { default: "loading" },
    intervalMs: { default: 3000 },
    scrambleDurationMs: { default: 1200 },
    frameIntervalMs: { default: 80 }
  },
  setup(__props: any) {

const props = __props as { loading: boolean, resolvedText: string | number, placeholder: string, intervalMs: number, scrambleDurationMs: number, frameIntervalMs: number }



const displayedText = ref("");
const resolvedValue = computed(() => String(props.resolvedText));
const isFinalizing = ref(false);

const randomPool = "!@#$%^&*()_+-=[]{}|;:,.<>/?~";
let intervalId: ReturnType<typeof setInterval> | null = null;
let rafId: number | null = null;
let cycleStartTs = 0;
let lastFrameTs = 0;

const randomChar = () => {
	const index = Math.floor(Math.random() * randomPool.length);
	return randomPool[index] ?? " ";
};

const buildRandomFrame = (base: string) => {
	if (!base.length) {
		return "";
	}

	let next = "";

	for (let i = 0; i < base.length; i += 1) {
		next += base[i] === " " ? " " : randomChar();
	}

	return next;
};

const buildRevealFrame = (target: string, progress: number) => {
	if (!target.length) {
		return "";
	}

	const revealCount = Math.floor(target.length * progress);
	let next = "";

	for (let i = 0; i < target.length; i += 1) {
		if (i < revealCount) {
			next += target[i];
		} else {
			next += target[i] === " " ? " " : randomChar();
		}
	}

	return next;
};

const stopInterval = () => {
	if (intervalId !== null) {
		clearInterval(intervalId);
		intervalId = null;
	}
};

const stopRaf = () => {
	if (rafId !== null) {
		cancelAnimationFrame(rafId);
		rafId = null;
	}
};

const resetCycle = () => {
	cycleStartTs = 0;
	lastFrameTs = 0;
};

const stopAll = () => {
	stopInterval();
	stopRaf();
	resetCycle();
	isFinalizing.value = false;
};

const renderResolved = () => {
	displayedText.value = resolvedValue.value;
};

const animateLoading = (timestamp: number) => {
	if (!props.loading) {
		return;
	}

	if (cycleStartTs === 0) {
		cycleStartTs = timestamp;
	}

	const duration = Math.max(1, props.scrambleDurationMs);
	const frameInterval = Math.max(16, props.frameIntervalMs);
	const elapsed = timestamp - cycleStartTs;
	const progress = Math.min(elapsed / duration, 1);

	if (lastFrameTs === 0 || timestamp - lastFrameTs >= frameInterval) {
		displayedText.value = buildRandomFrame(props.placeholder);
		lastFrameTs = timestamp;
	}

	if (progress < 1) {
		rafId = requestAnimationFrame(animateLoading);
	} else {
		rafId = null;
	}
};

const animateFinalReveal = (timestamp: number) => {
	if (props.loading) {
		return;
	}

	if (cycleStartTs === 0) {
		cycleStartTs = timestamp;
	}

	const duration = Math.max(1, props.scrambleDurationMs);
	const frameInterval = Math.max(16, props.frameIntervalMs);
	const elapsed = timestamp - cycleStartTs;
	const progress = Math.min(elapsed / duration, 1);

	if (lastFrameTs === 0 || timestamp - lastFrameTs >= frameInterval) {
		displayedText.value = buildRevealFrame(resolvedValue.value, progress);
		lastFrameTs = timestamp;
	}

	if (progress < 1) {
		rafId = requestAnimationFrame(animateFinalReveal);
	} else {
		rafId = null;
		isFinalizing.value = false;
		renderResolved();
	}
};

const startCycle = () => {
	if (!props.loading) {
		return;
	}

	stopRaf();
	resetCycle();
	rafId = requestAnimationFrame(animateLoading);
};

const startFinalReveal = () => {
	stopInterval();
	stopRaf();
	resetCycle();
	isFinalizing.value = true;

	if (!displayedText.value) {
		displayedText.value = buildRandomFrame(resolvedValue.value);
	}

	rafId = requestAnimationFrame(animateFinalReveal);
};

const startLoadingAnimation = () => {
	isFinalizing.value = false;
	stopInterval();
	startCycle();

	intervalId = setInterval(() => {
		startCycle();
	}, Math.max(1, props.intervalMs));
};

watch(
	() => props.loading,
	(isLoading, wasLoading) => {
		if (isLoading) {
			startLoadingAnimation();
			return;
		}

		if (wasLoading) {
			startFinalReveal();
			return;
		}

		stopAll();
		renderResolved();
	},
	{ immediate: true }
);

watch([() => props.placeholder, () => props.intervalMs, () => props.scrambleDurationMs, () => props.frameIntervalMs], () => {
	if (props.loading) {
		startLoadingAnimation();
	}
});

watch(
	() => props.resolvedText,
	() => {
		if (!props.loading && !isFinalizing.value) {
			renderResolved();
		}
	}
);

onUnmounted(() => {
	stopAll();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", {
    class: "text-scramble-loader",
    "aria-live": "polite",
    "aria-busy": __props.loading ? 'true' : 'false'
  }, _toDisplayString(displayedText.value), 9, _hoisted_1))
}
}

})